import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface License {
  id: number;
  license_key: string;
  issued_at: string;
  expires_at: string;
  status: string;
  days_until_expiry: number;
}

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const [license, setLicense] = useState<License | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLicense();
  }, []);

  const fetchLicense = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer/license`);
      setLicense(response.data);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to load license');
    } finally {
      setLoading(false);
    }
  };

  const downloadLicense = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer/download-license`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `license-${user?.customer_id}.env`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading license:', error);
      alert('Failed to download license file');
    }
  };

  const copyToClipboard = () => {
    if (license) {
      navigator.clipboard.writeText(license.license_key);
      alert('License key copied to clipboard!');
    }
  };

  const getStatusColor = () => {
    if (!license) return 'gray';
    if (license.status === 'revoked') return 'red';
    if (license.days_until_expiry < 0) return 'red';
    if (license.days_until_expiry < 30) return 'orange';
    return 'green';
  };

  const getStatusText = () => {
    if (!license) return 'Unknown';
    if (license.status === 'revoked') return 'Revoked';
    if (license.days_until_expiry < 0) return 'Expired';
    if (license.days_until_expiry < 30) return 'Expiring Soon';
    return 'Active';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading license information...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">License Portal</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button 
              onClick={logout}
              className="text-sm text-blue-600 hover:underline"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* License Information */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Your License</h2>
          
          {license ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm text-gray-600">Status</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-block w-3 h-3 rounded-full bg-${getStatusColor()}-500`}></span>
                    <span className="font-medium">{getStatusText()}</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Customer ID</label>
                  <p className="font-medium mt-1">{user?.customer_id}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Issued</label>
                  <p className="font-medium mt-1">
                    {new Date(license.issued_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600">Expires</label>
                  <p className="font-medium mt-1">
                    {new Date(license.expires_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="col-span-2">
                  <label className="text-sm text-gray-600">Days Until Expiry</label>
                  <p className="text-2xl font-bold mt-1">
                    {license.days_until_expiry > 0 ? license.days_until_expiry : 0} days
                  </p>
                </div>
              </div>

              {/* Warning Messages */}
              {license.days_until_expiry < 30 && license.days_until_expiry > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded p-4 mb-4">
                  <p className="text-orange-800 font-medium">
                    ⚠️ Your license expires in {license.days_until_expiry} days
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    Please contact sales@yourcompany.com to renew your license.
                  </p>
                </div>
              )}

              {license.days_until_expiry <= 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
                  <p className="text-red-800 font-medium">
                    🚫 Your license has expired
                  </p>
                  <p className="text-red-700 text-sm mt-1">
                    Please renew immediately to continue using the software.
                  </p>
                </div>
              )}

              {/* License Key */}
              <div className="bg-gray-50 rounded p-4 mb-4">
                <label className="text-sm text-gray-600 block mb-2">License Key</label>
                <code className="block text-xs bg-white p-3 rounded border break-all font-mono">
                  {license.license_key}
                </code>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={downloadLicense}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Download License File
                </button>
                
                <button 
                  onClick={copyToClipboard}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Copy to Clipboard
                </button>
              </div>
            </>
          ) : (
            <p className="text-gray-500">No active license found. Please contact support.</p>
          )}
        </div>

        {/* Installation Instructions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-3">Installation Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>Download your license file using the button above</li>
            <li>Add the LICENSE_KEY to your .env file in your deployment</li>
            <li>Restart your Docker containers: <code className="bg-gray-100 px-2 py-1 rounded">docker-compose restart</code></li>
            <li>Verify the license is active in your application logs</li>
          </ol>
        </div>

        {/* Support Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3">Need Help?</h3>
          <p className="text-sm text-gray-700 mb-2">
            Contact our support team:
          </p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li><strong>Email:</strong> support@yourcompany.com</li>
            <li><strong>Sales:</strong> sales@yourcompany.com</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
